/**
 * SalesAuditStrip.tsx — the Slice B audit strip (§15.5 frontend surface).
 *
 * One reverse-chronological list per record: `sales_field_history` for every
 * non-stage field edit, plus `sales_deal_stage_events` for stage transitions
 * on a deal. The two reads stay SEPARATE on the wire (B-1) and are composed
 * client-side in auditStrip.ts — see the ordering rule there.
 *
 * DATA HONESTY, the whole point of this component:
 *  - A row with no actor renders as an unknown user. The six pre-0117 stage
 *    events were made by real people whose identity was never recorded, so
 *    "system" would be a fabrication (B-2) and a blank would hide a real
 *    change. Neither is acceptable; the strip says it does not know.
 *  - An attributed row shows the actor's display name when the read path
 *    carried one (§15.12 AN-3: resolved server-side, display_name only, never
 *    an email) and the SHORT user id when it did not; the full id is always
 *    in the title. The ladder lives in auditStrip.ts (actorFor/actorLabel) —
 *    the single change point AN-4 names.
 *  - `assigned_employee_id` and `account_id` resolve to names through a
 *    FRONTEND join. A join target that has gone (a deleted account, a leaver
 *    filtered out of the roster) falls back to the raw id, never a blank.
 *  - NULL and "" are different stored values (B-5) and render differently.
 *
 * Looks: ADOPTION.md §3.4 — `.k-card` with `.k-card-head`, rows on
 * `.k-cell-muted` weights, the Deal Detail timeline's 8x8 icon medallion so
 * the strip reads as a sibling of the activity timeline it sits under.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { ArrowRight, Layers, Pencil, Plus } from 'lucide-react'
import { useAsync, fetchFieldHistory, fetchDealStageEvents, fetchAccounts, fetchDirectory, relAgo, fmtDate } from '../../api'
import {
  composeAudit, fieldLabel, renderValue, stageLabel, actorFor, actorLabel,
  type AuditRow, type AuditJoins, type RenderedValue,
} from './auditStrip'

const KIND_ICON = {
  stage: <Layers size={13} />,
  field: <Pencil size={13} />,
  created: <Plus size={13} />,
}

/** An empty value is styled apart from a real one, and the two KINDS of empty
 *  are labelled apart from each other (B-5). */
function Value({ v }: { v: RenderedValue }) {
  if (v.kind === 'empty') return <span className="text-ink-faint italic">empty</span>
  if (v.kind === 'blank') return <span className="text-ink-faint italic">blank</span>
  return <span className="text-ink-primary">{v.text}</span>
}

function Row({ row, joins }: { row: AuditRow; joins: AuditJoins }) {
  const actor = actorFor(row)
  const who = actorLabel(actor)

  let title: React.ReactNode
  let detail: React.ReactNode = null

  if (row.kind === 'created') {
    // The birth row is a DIFFERENT SHAPE from a field change on purpose: it
    // has no before and no after, and reading it as "created → null" would
    // be nonsense.
    title = <>Record created</>
  } else if (row.kind === 'stage') {
    title = row.oldValue === null
      ? <>Entered {stageLabel(row.newValue ?? '')}</>
      : <>Stage {stageLabel(row.oldValue)} <ArrowRight size={11} className="inline align-middle text-ink-faint" /> {stageLabel(row.newValue ?? '')}</>
  } else {
    title = <>{fieldLabel(row.field)} changed</>
    detail = (
      <div className="text-12 mt-1 flex items-center gap-2 flex-wrap">
        <Value v={renderValue(row.field, row.oldValue, joins)} />
        <ArrowRight size={11} className="text-ink-faint flex-shrink-0" />
        <Value v={renderValue(row.field, row.newValue, joins)} />
      </div>
    )
  }

  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-surface-panel border border-edge-row flex items-center justify-center text-ink-faint flex-shrink-0">
        {KIND_ICON[row.kind]}
      </div>
      <div className="min-w-0">
        <div className="text-13 font-semibold text-ink-primary">{title}</div>
        {detail}
        <div className="text-11 text-ink-faint mt-1">
          <span
            className={actor.kind === 'unattributed' ? 'italic' : undefined}
            title={actor.kind === 'user' ? `User ${actor.userId}` : 'This change predates recorded attribution'}
          >{who}</span>
          {' · '}{relAgo(row.at)} · {fmtDate(row.at)}
        </div>
      </div>
    </div>
  )
}

export function SalesAuditStrip({ recordType, recordId, withStageEvents = false, variant = 'card' }: {
  recordType: 'deals' | 'contacts' | 'accounts'
  recordId: string
  /** Deals only: also read and compose the stage transitions (B-1 keeps the
   *  two reads separate, so this is a SECOND request, never a merged one). */
  withStageEvents?: boolean
  /** 'card' = a standalone `.k-card` for the two detail PAGES. 'bare' = rows
   *  only, for the contact drawer, whose sections already carry their own
   *  `.k-eyebrow` heading — a card inside a drawer section would double the
   *  title and break the drawer's flat rhythm. */
  variant?: 'card' | 'bare'
}) {
  const historyQ = useAsync(() => fetchFieldHistory(recordType, recordId), [recordType, recordId])
  const stageQ = useAsync(
    () => (withStageEvents ? fetchDealStageEvents(recordId) : Promise.resolve([])),
    [recordId, withStageEvents])

  // Join targets for the two id-valued fields. Both are best-effort: a failed
  // roster read degrades the strip to raw ids, it never blanks a row or
  // hides the change.
  const employeesQ = useAsync(() => fetchDirectory(), [])
  const accountsQ = useAsync(() => fetchAccounts(), [])
  const joins: AuditJoins = {
    employeeName: (id) => (employeesQ.data ?? []).find(e => e.id === id)?.fullName,
    accountName: (id) => (accountsQ.data ?? []).find(a => a.id === id)?.name,
  }

  const loading = historyQ.loading || (withStageEvents && stageQ.loading)
  const rows = composeAudit(historyQ.data ?? [], stageQ.data ?? [])

  // A failed history read is NOT an empty history — saying "no changes" over
  // a 500 would state something we do not know.
  const failed = historyQ.error || (withStageEvents && stageQ.error)

  const body = (
    <>
      {loading && <div className="text-13 text-ink-muted">Loading history…</div>}

      {!loading && failed && (
        <div className="text-13 text-ink-muted">
          Change history unavailable.
        </div>
      )}

      {!loading && !failed && rows.length === 0 && (
        <div className="text-13 text-ink-muted">
          No changes recorded.
          <div className="text-12 text-ink-faint mt-1">
            Records last edited before change history was switched on have no entries.
          </div>
        </div>
      )}

      {!loading && !failed && rows.map(r => <Row key={r.key} row={r} joins={joins} />)}
    </>
  )

  if (variant === 'bare') {
    return <div className="flex flex-col gap-4 pb-2">{body}</div>
  }

  return (
    <div className="k-card">
      <div className="k-card-head">
        <div>
          <div className="k-card-title">Change history</div>
          <div className="k-card-sub">
            {withStageEvents ? 'Field edits and stage transitions' : 'Field edits'}
          </div>
        </div>
      </div>
      <div className="p-5 flex flex-col gap-4">{body}</div>
    </div>
  )
}
